import { SagaIterator } from 'redux-saga'
import {
  all,
  call,
  fork,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects'

import {
  loadingAR,
  setActividadReservasData,
  setGeneratedStart,
} from './actions'

import {
  ACTIVIDAD_RESERVA_CLEAR,
  ACTIVIDAD_RESERVA_GET,
  ACTIVIDAD_RESERVA_START_GENERATED,
  ACTIVIDAD_RESERVA_SEARCH,
  ACTIVIDAD_RESERVA_IS_SEARCH,
  ACTIVIDAD_RESERVA_ALL_GENERATED,
  ACTIVIDAD_RESERVA_UPDATE_BLOQUEO,
  ACTIVIDAD_RESERVA_UPDATE_SELECTED,
} from './types'

import { RootState } from '../../store'
import { ACTIVIDAD_RESERVA_DETALLE_CLEAR } from '../actividadReservaDetalles/types'
import { setTotalesReservas } from '../reservas/reservasOperacionesSagas'
import { RESER_OPS_UPDATE_TRESERVAS_BLOQUEADAS } from '../reservas/types'
import {
  RESERVAS_ACTIONS_OPEN_GENERATED,
  RESERVAS_ACTIONS_OPEN_MULTI_BLOCK,
  RESERVAS_ACTIONS_SET_IS_PROCESSING_MULTI_BLOCK,
} from '../reservasActions/type'

import { notifications } from '@/common/helpers/notifications'
import {
  contarActividadesBloqueadas,
  getActividadReservaBusiness,
  getReservasTipoEstados,
  multiProcessGenerarReservas,
  postGenerarReservas,
} from '@/common/helpers/reservasServices'
import { Action } from '@/common/interfaces/general'

function* generarReservasBy(): SagaIterator {
  const { reservasBloqueadas } = yield select(
    (state: RootState) => state.ReservasOperaciones,
  )

  const { selected } = yield select(
    (state: RootState) => state.ActividadesReservas,
  )
  const { data } = yield select(
    (state: RootState) => state.ActividadReservasDetalles,
  )
  const { tipo } = yield select((state: RootState) => state.ReservasActions)
  const { estados } = yield select((state: RootState) => state.Catalogos)

  try {
    yield put(setGeneratedStart(true))
    yield call(postGenerarReservas, selected, data)
    yield put({ type: ACTIVIDAD_RESERVA_UPDATE_BLOQUEO, payload: selected })
    yield put({
      type: ACTIVIDAD_RESERVA_UPDATE_SELECTED,
      payload: { ...selected, bloqueoReserva: true },
    })
    const reservaData = yield call(
      getReservasTipoEstados,
      tipo,
      [
        estados['Reserva']['Reserva Pendiente'].EstadoId,
        estados['Reserva']['Modificación Pendiente'].EstadoId,
        estados['Reserva']['Cancelación Pendiente'].EstadoId,
      ],
      '',
      estados['Reserva'],
    ) // Tipo Alimento / Hotel

    yield call(setTotalesReservas, reservaData, [])
    yield put({
      type: RESER_OPS_UPDATE_TRESERVAS_BLOQUEADAS,
      payload: reservasBloqueadas - 1,
    })
    yield put({ type: RESERVAS_ACTIONS_OPEN_GENERATED, payload: false })
    notifications({
      title: 'Éxito',
      message: 'Bloqueado',
      toastType: 'success',
    })
  } catch (error) {
    yield put({ type: RESERVAS_ACTIONS_OPEN_GENERATED, payload: false })
    notifications({ ex: error })
  } finally {
    yield put(setGeneratedStart(false))
  }
}

function* getActividadesReservas({ payload }: Action<string>): SagaIterator {
  try {
    yield put(loadingAR(true))
    yield put({ type: ACTIVIDAD_RESERVA_CLEAR })
    yield put({ type: ACTIVIDAD_RESERVA_DETALLE_CLEAR })

    const actividadReservas = yield call(getActividadReservaBusiness, payload)

    yield put(setActividadReservasData(actividadReservas))

    const arb = contarActividadesBloqueadas(actividadReservas)

    yield put({ type: RESER_OPS_UPDATE_TRESERVAS_BLOQUEADAS, payload: arb })
  } catch (error) {
    notifications({ ex: error })
  } finally {
    yield put(loadingAR(false))
  }
}

function* searchByPeriodo({ payload }: Action<{ inicio?: Date; fin?: Date } | undefined>): SagaIterator {
  const { tipo } = yield select((state: RootState) => state.ReservasActions)
  const inicio = payload !== undefined ? payload.inicio : undefined
  const fin = payload !== undefined ? payload.fin : undefined

  try {
    yield put({ type: ACTIVIDAD_RESERVA_IS_SEARCH, payload: true })
    yield put({ type: ACTIVIDAD_RESERVA_CLEAR })
    yield put({ type: ACTIVIDAD_RESERVA_DETALLE_CLEAR })

    const actividadReservas = yield call(
      getActividadReservaBusiness,
      tipo,
      inicio,
      fin,
    )

    yield put(setActividadReservasData(actividadReservas))

    const arb = contarActividadesBloqueadas(actividadReservas)

    yield put({ type: RESER_OPS_UPDATE_TRESERVAS_BLOQUEADAS, payload: arb })
  } catch (error) {
    notifications({ ex: error })
  } finally {
    yield put({ type: ACTIVIDAD_RESERVA_IS_SEARCH, payload: false })
  }
}

function* generarTodo(): SagaIterator {
  try {
    yield put({
      type: RESERVAS_ACTIONS_SET_IS_PROCESSING_MULTI_BLOCK,
      payload: true,
    })

    const { tipo, currentBookings } = yield select(
      (state: RootState) => state.ReservasActions,
    )
    const { estados } = yield select((state: RootState) => state.Catalogos)

    yield call(multiProcessGenerarReservas, currentBookings, tipo)
    yield put({ type: RESERVAS_ACTIONS_OPEN_MULTI_BLOCK, payload: false })
    yield put({ type: ACTIVIDAD_RESERVA_GET, payload: tipo })
    const reservaData = yield call(
      getReservasTipoEstados,
      tipo,
      [
        estados['Reserva']['Reserva Pendiente'].EstadoId,
        estados['Reserva']['Modificación Pendiente'].EstadoId,
        estados['Reserva']['Cancelación Pendiente'].EstadoId,
      ],
      '',
      estados['Reserva'],
    )

    yield call(setTotalesReservas, reservaData, [])
    notifications({
      title: 'Éxito',
      message: `Se bloquearon con éxito`,
      toastType: 'success',
    })
  } catch (error) {
    yield put({ type: RESERVAS_ACTIONS_OPEN_MULTI_BLOCK, payload: false })
    notifications({ ex: error })
  } finally {
    yield put({
      type: RESERVAS_ACTIONS_SET_IS_PROCESSING_MULTI_BLOCK,
      payload: false,
    })
  }
}

// observadores
function* watchAllGenerated(): Generator {
  yield takeLatest(ACTIVIDAD_RESERVA_ALL_GENERATED, generarTodo)
}

function* watchSearchReserva(): Generator {
  yield takeLatest(ACTIVIDAD_RESERVA_SEARCH, searchByPeriodo)
}

function* watchGenerarReservas(): Generator {
  yield takeEvery(ACTIVIDAD_RESERVA_START_GENERATED, generarReservasBy)
}

function* watchActividadesReservas(): Generator {
  yield takeEvery(ACTIVIDAD_RESERVA_GET, getActividadesReservas)
}

export default function* actividadesReservasSagas(): Generator {
  yield all([
    fork(watchGenerarReservas),
    fork(watchActividadesReservas),
    fork(watchSearchReserva),
    fork(watchAllGenerated),
  ])
}
